/**
 * useSubscription Hook
 *
 * Custom React hook for managing user premium subscriptions
 * Provides subscription status, creation, and cancellation functionality
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { cancelPendingBackendUnavailableBanner, setBackendUnavailable } from '../utils/backendUnavailable';
import { resolveApiUrl } from '../utils/subdomainUtils';
import { logger } from "../utils/logger";
import type { AdEligibility, AdFreeReason } from '../../shared/types/adEligibility';
import { isViteFlagEnabled } from '../utils/runtimePlatformFlags';
import {
    applyAdFreeDomBlocking,
    cacheAdEligibilityLocal,
    clearAdEligibilityLocalCache,
    readCachedShowAds,
} from '../utils/applyAdFreeDomBlocking';
import { userHasK9RoleFromUser } from '../utils/userK9RoleHint';
import {
  isStripeHostedRedirectUrl,
  sanitizeHttpUrlForHref,
} from '../../shared/utils/sanitizeHttpUrlForHref';
// import { logger } from "../utils/logger";
export interface PremiumStatus {
    hasPremium: boolean;
    source: 'none' | 'user_subscription' | 'agency_contract' | 'both';
    subscriptionTier?: 'free' | 'premium';
    subscriptionStatus?: 'active' | 'inactive' | 'canceled' | 'past_due';
    subscriptionType?: 'monthly' | 'annual' | 'agency_contract';
    subscriptionEndDate?: string | null;
    agencyContractTier?: 'none' | 'basic' | 'professional' | 'enterprise';
    agencyContractEndDate?: string | null;
}
export interface SubscriptionPlan {
    id: string;
    name: string;
    price: number;
    currency: string;
    interval: string;
    priceId: string;
    savings?: number;
    savingsPercent?: number;
    features: string[];
}
export interface SubscriptionHistory {
    id: number;
    userId: number;
    subscriptionTier: 'free' | 'premium';
    subscriptionType?: 'monthly' | 'annual' | 'agency_contract';
    action: 'created' | 'renewed' | 'canceled' | 'upgraded' | 'downgraded' | 'payment_failed';
    amount?: number;
    stripePaymentId?: string;
    stripeSubscriptionId?: string;
    metadata?: any;
    createdAt: string;
}
const useSubscription = () => {
    const { user, isAuthenticated, isInitializing } = useAuth();
    const [premiumStatus, setPremiumStatus] = useState<PremiumStatus | null>(null);
    const [adEligibility, setAdEligibility] = useState<AdEligibility | null>(null);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [history, setHistory] = useState<SubscriptionHistory[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Refs to prevent duplicate requests
    const fetchingStatus = useRef(false);
    const fetchingPlans = useRef(false);
    const lastFetchedUserId = useRef<string | number | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const lastStatusFetchAt = useRef<number>(0);
    // Computed values
    const hasPremium = premiumStatus?.hasPremium || false;
    const isPremiumUser = premiumStatus?.subscriptionStatus === 'active' && premiumStatus?.subscriptionTier === 'premium';
    const hasAgencyContract = premiumStatus?.source === 'agency_contract' || premiumStatus?.source === 'both';
    const showAds = useMemo(() => {
        if (adEligibility != null) {
            return adEligibility.showAds;
        }
        const cachedShowAds = readCachedShowAds();
        if (cachedShowAds !== null) {
            return cachedShowAds;
        }
        if (hasPremium) {
            return false;
        }
        const k9HintEnabled = isViteFlagEnabled('VITE_K9_AD_FREE_ENABLED', import.meta.env.VITE_K9_AD_FREE_ENABLED);
        const userOffline = Boolean((user as { offline?: boolean } | null)?.offline);
        if (user && userHasK9RoleFromUser(user) && (k9HintEnabled || userOffline)) {
            return false;
        }
        return true;
    }, [adEligibility, hasPremium, user]);
    const isAdFree = !showAds;
    // Only show CTA if authenticated, not loading, and user doesn't have premium
    const showUpgradeCTA = isAuthenticated && !isLoading && !hasPremium;
    /**
     * Fetch subscription status with improved error handling and debouncing
     */
    const fetchSubscriptionStatus = useCallback(async (force = false) => {
        // Skip subscription checks on public/embed pages
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/embed/') || currentPath.includes('embed')) {
            if (process.env.NODE_ENV === 'development') {
                logger.debug('🔍 useSubscription - On embed page, skipping subscription check');
            }
            setPremiumStatus({
                hasPremium: false,
                source: 'none',
                subscriptionTier: 'free',
                subscriptionStatus: 'inactive',
            });
            setAdEligibility({
                showAds: true,
                reasons: [],
                k9: { hasK9Role: false, k9Roles: [] },
            });
            cacheAdEligibilityLocal(true, false);
            setIsLoading(false);
            setError(null);
            return;
        }
        // Auth still bootstrapping — do not force showAds=true or clobber an ad-free
        // local cache (that caused a 120px AdSense reserve to open then close under the header).
        if (isInitializing) {
            setPremiumStatus({
                hasPremium: false,
                source: 'none',
                subscriptionTier: 'free',
                subscriptionStatus: 'inactive',
            });
            setAdEligibility(null);
            setIsLoading(true);
            setError(null);
            return;
        }
        // Confirmed guest (auth finished, not signed in)
        if (!isAuthenticated || !user) {
            if (process.env.NODE_ENV === 'development') {
                logger.auth('🔍 useSubscription - User not authenticated, setting free status', {
                    arg0: {
                        isAuthenticated,
                        hasUser: !!user,
                        isInitializing,
                    },
                });
            }
            setPremiumStatus({
                hasPremium: false,
                source: 'none',
                subscriptionTier: 'free',
                subscriptionStatus: 'inactive',
            });
            setAdEligibility({
                showAds: true,
                reasons: [],
                k9: { hasK9Role: false, k9Roles: [] },
            });
            cacheAdEligibilityLocal(true, false);
            setIsLoading(false);
            setError(null);
            return;
        }
        // Prevent duplicate requests unless forced
        if (fetchingStatus.current && !force) {
            if (process.env.NODE_ENV === 'development') {
                logger.api('🔍 useSubscription - Already fetching status, skipping duplicate request', "GET");
            }
            return;
        }
        // Check if we already fetched for this user (prevent redundant calls)
        // BUT: Always refetch if premium status is false (might have been updated)
        const shouldSkip = !force && lastFetchedUserId.current === user.id;
        if (shouldSkip && premiumStatus?.hasPremium) {
            if (process.env.NODE_ENV === 'development') {
                logger.debug('🔍 useSubscription - Status already fetched for this user, skipping');
            }
            return;
        }
        // If we have cached false premium status, force refetch to check for updates
        if (shouldSkip && premiumStatus?.hasPremium === false) {
            if (process.env.NODE_ENV === 'development') {
                logger.debug('🔄 useSubscription - Premium status is false, forcing refetch to check for updates');
            }
            // Continue to fetch below
        }
        else if (shouldSkip) {
            return;
        }
        fetchingStatus.current = true;
        try {
            setIsLoading(true);
            setError(null);
            if (process.env.NODE_ENV === 'development') {
                logger.debug('🔍 useSubscription - Fetching subscription status for user:', { "arg0": user.email });
            }
            const response = await axios.get(resolveApiUrl('/api/subscription/status'), {
                withCredentials: true,
                timeout: 15000, // 15 second timeout (increased for slow connections)
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });
            if (response.data.success) {
                cancelPendingBackendUnavailableBanner();
                const fetchedPremiumStatus = response.data.premiumStatus;
                const hasPremiumValue = fetchedPremiumStatus?.hasPremium || false;
                const fetchedAdEligibility: AdEligibility | null = response.data.adEligibility ?? null;
                const resolvedShowAds =
                    fetchedAdEligibility?.showAds ?? !hasPremiumValue;
                const resolvedReasons: AdEligibility['reasons'] =
                    fetchedAdEligibility?.reasons ??
                    (hasPremiumValue ? ['premium'] : []);
                logger.debug('[useSubscription] ✅ Successfully fetched subscription status', { "arg0": {
                        hasPremium: hasPremiumValue,
                        showAds: resolvedShowAds,
                        adReasons: resolvedReasons,
                        source: fetchedPremiumStatus?.source,
                        subscriptionTier: fetchedPremiumStatus?.subscriptionTier,
                        subscriptionStatus: fetchedPremiumStatus?.subscriptionStatus,
                        userId: user.id,
                        email: user.email,
                    } });
                setPremiumStatus(fetchedPremiumStatus);
                if (fetchedAdEligibility) {
                    setAdEligibility(fetchedAdEligibility);
                } else {
                    setAdEligibility({
                        showAds: !hasPremiumValue,
                        reasons: hasPremiumValue ? ['premium'] : [],
                        k9: { hasK9Role: false, k9Roles: [] },
                    });
                }
                cacheAdEligibilityLocal(resolvedShowAds, hasPremiumValue);
                applyAdFreeDomBlocking(resolvedReasons);
                if (response.data.history) {
                    setHistory(response.data.history);
                }
                lastFetchedUserId.current = user.id; // Mark as fetched for this user
                // Log a warning if user should have premium but doesn't
                if (!hasPremiumValue && user.email) {
                    logger.debug(`[useSubscription] ℹ️ User ${user.email} does not have premium access`);
                }
                else if (hasPremiumValue) {
                    logger.debug(`[useSubscription] ✅ Premium access confirmed for ${user.email}`);
                }
            }
            else {
                logger.api('⚠️ useSubscription - Response indicated failure:', response.data);
                throw new Error(response.data.error || 'Unknown server error');
            }
        }
        catch (err: any) {
            const status = err.response?.status;
            const isServiceUnavailable = status === 503 || err.response?.data?.code === 'DEV_BACKEND_UNREACHABLE';
            if (isServiceUnavailable) {
                // Dev backend not running (Vite proxy returns 503) - graceful fallback, no console spam
                if (process.env.NODE_ENV === 'development') {
                    logger.debug('🔍 useSubscription - Backend unavailable (503), using default free status');
                }
                setError(null);
            } else {
                logger.api('❌ useSubscription - Error fetching subscription status:', {
                    error: err.message,
                    response: err.response?.data,
                    status,
                    userId: user?.id,
                    email: user?.email,
                    stack: err.stack
                });
                // Handle different error types
                if (status === 401) {
                    // Authentication error - don't set error state, just set free status
                    if (process.env.NODE_ENV === 'development') {
                        logger.error('🔍 useSubscription - 401 error, user may need to re-authenticate', undefined, {});
                    }
                    setError(null); // Don't show error to user for auth issues
                }
                else if (status === 404) {
                    // API not found - silently handle
                    setError(null);
                }
                else {
                    setError(err.response?.data?.error || 'Failed to fetch subscription status');
                }
            }
            // Always set default free status on error
            setPremiumStatus({
                hasPremium: false,
                source: 'none',
                subscriptionTier: 'free',
                subscriptionStatus: 'inactive',
            });
            setAdEligibility({
                showAds: true,
                reasons: [],
                k9: { hasK9Role: false, k9Roles: [] },
            });
            cacheAdEligibilityLocal(true, false);
            applyAdFreeDomBlocking([]);
            // Mark this user as fetched so 503/401 fallbacks do not retrigger refetch loops
            if (user?.id) {
                lastFetchedUserId.current = user.id;
            }
        }
        finally {
            setIsLoading(false);
            fetchingStatus.current = false;
            lastStatusFetchAt.current = Date.now();
        }
    }, [isAuthenticated, user, isInitializing, premiumStatus?.hasPremium]);
    /**
     * Fetch available subscription plans (with duplicate request prevention)
     */
    const fetchPlans = useCallback(async () => {
        // Prevent duplicate requests
        if (fetchingPlans.current) {
            logger.api('🔍 useSubscription - Already fetching plans, skipping duplicate request', "GET");
            return;
        }
        // If we already have plans, don't fetch again
        if (plans && plans.length > 0) {
            logger.debug('🔍 useSubscription - Plans already loaded, skipping');
            return;
        }
        fetchingPlans.current = true;
        try {
            logger.debug('🔍 useSubscription - Fetching subscription plans');
            const response = await axios.get(resolveApiUrl('/api/subscription/plans'), {
                withCredentials: true,
                timeout: 10000,
            });
            if (response.data.success) {
                cancelPendingBackendUnavailableBanner();
                logger.debug('✅ useSubscription - Successfully fetched plans');
                setPlans(response.data.plans || []);
            }
        }
        catch (err: any) {
            const isServiceUnavailable = err.response?.status === 503 || err.response?.data?.code === 'DEV_BACKEND_UNREACHABLE';
            if (isServiceUnavailable) {
                if (typeof window !== 'undefined' && (window.location.port === '5173' || window.location.port === '5174')) {
                    setBackendUnavailable();
                }
                // Dev backend not running - leave plans as [], no console spam
                if (process.env.NODE_ENV === 'development') {
                    logger.debug('🔍 useSubscription - Backend unavailable (503), subscription plans not loaded');
                }
            } else {
                logger.error('❌ useSubscription - Error fetching subscription plans:', err, {});
            }
        }
        finally {
            fetchingPlans.current = false;
        }
    }, [plans?.length || 0]);
    /**
     * Create a new subscription
     */
    const createSubscription = async (subscriptionType: 'monthly' | 'annual'): Promise<{
        sessionUrl: string;
    }> => {
        try {
            setError(null);
            const response = await axios.post(resolveApiUrl('/api/subscription/create'), {
                subscriptionType,
            }, {
                withCredentials: true,
            });
            if (response.data.success) {
                // Redirect to Stripe Checkout
                return {
                    sessionUrl: response.data.sessionUrl,
                };
            }
            else {
                throw new Error(response.data.error || 'Failed to create subscription');
            }
        }
        catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Failed to create subscription';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };
    /**
     * Cancel active subscription
     */
    const cancelSubscription = async (): Promise<void> => {
        try {
            setError(null);
            const response = await axios.post(resolveApiUrl('/api/subscription/cancel'), {}, {
                withCredentials: true,
            });
            if (response.data.success) {
                // Refresh subscription status
                await fetchSubscriptionStatus();
            }
            else {
                throw new Error(response.data.error || 'Failed to cancel subscription');
            }
        }
        catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Failed to cancel subscription';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };
    /**
     * Fetch subscription history
     */
    const fetchHistory = async (limit: number = 50): Promise<void> => {
        try {
            const response = await axios.get(resolveApiUrl(`/api/subscription/history?limit=${limit}`), {
                withCredentials: true,
            });
            if (response.data.success) {
                setHistory(response.data.history);
            }
        }
        catch (err: any) {
            logger.error('Error fetching subscription history:', err, {});
        }
    };
    /**
     * Open Stripe Customer Portal
     */
    const openCustomerPortal = async (): Promise<void> => {
        try {
            const response = await axios.post(resolveApiUrl('/api/subscription/portal'), {}, {
                withCredentials: true,
            });
            if (response.data.success && response.data.portalUrl) {
                const portalUrl = response.data.portalUrl as string;
                if (!isStripeHostedRedirectUrl(portalUrl)) {
                    throw new Error('Invalid portal URL from server');
                }
                const safe = sanitizeHttpUrlForHref(portalUrl);
                if (!safe) {
                    throw new Error('Invalid portal URL from server');
                }
                window.location.href = safe;
            }
            else {
                throw new Error(response.data.error || 'Failed to open customer portal');
            }
        }
        catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Failed to open customer portal';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };
    // Debounced fetch function to prevent rapid calls
    const debouncedFetch = useCallback(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            if (isAuthenticated && user) {
                fetchSubscriptionStatus();
            }
        }, 300); // 300ms debounce
    }, [isAuthenticated, user, fetchSubscriptionStatus]);
    // Plans are fetched on demand (e.g. PremiumPage) to avoid /api/subscription/plans on every route.
    // Fetch status when authentication state changes
    useEffect(() => {
        if (isAuthenticated && user) {
            // Clear debounce timeout on cleanup
            return () => {
                if (debounceTimeout.current) {
                    clearTimeout(debounceTimeout.current);
                }
            };
        }
        else {
            // Reset state when user logs out
            setPremiumStatus({
                hasPremium: false,
                source: 'none',
                subscriptionTier: 'free',
                subscriptionStatus: 'inactive',
            });
            setAdEligibility(null);
            clearAdEligibilityLocalCache();
            applyAdFreeDomBlocking([]);
            setHistory([]);
            setError(null);
            setIsLoading(false);
            lastFetchedUserId.current = null;
        }
    }, [isAuthenticated, user]);
    // Reset and refetch when user ID changes (e.g., during impersonation)
    const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastRefetchAttempt = useRef<number>(0);
    useEffect(() => {
        // Wait for auth initialization to complete before making API calls
        if (user?.id && isAuthenticated && !isInitializing) {
            const now = Date.now();
            const timeSinceLastRefetch = now - lastRefetchAttempt.current;
            const REFETCH_COOLDOWN = 5000; // 5 seconds cooldown to prevent infinite loops
            const STATUS_REVALIDATE_MS = 5 * 60 * 1000; // 5 minutes
            // Determine if we need to refetch:
            // 1. If we haven't fetched for this user yet
            // 2. If premium status is null (initial state)
            // 3. If premium status has source: 'none' and hasPremium is false - this indicates a stale pre-authentication status
            //    ALWAYS refetch when auth completes, regardless of cooldown (this is a critical case)
            // 4. If premium status is false from a post-auth fetch - respect cooldown to prevent infinite loops
            const hasNotFetched = user.id !== lastFetchedUserId.current;
            const isNullStatus = premiumStatus === null;
            // Pre-auth placeholder uses source 'none' + hasPremium false before lastFetchedUserId is set.
            // Post-auth free users also use source 'none' — only treat as stale when we never fetched for this user.
            const isStalePreAuthStatus =
                hasNotFetched &&
                premiumStatus?.source === 'none' &&
                premiumStatus?.hasPremium === false;
            // If status is false from a post-auth fetch, refetch after cooldown
            const timeSinceLastStatusFetch = now - lastStatusFetchAt.current;
            const shouldRevalidateStatus =
                !hasNotFetched &&
                timeSinceLastStatusFetch > STATUS_REVALIDATE_MS;
            // Check if we need to refetch - we'll check fetchingStatus.current inside the timeout callback
            // to avoid race conditions where a fetch completes between the check and the timeout firing
            const needsRefetch =
                hasNotFetched ||
                isNullStatus ||
                isStalePreAuthStatus ||
                shouldRevalidateStatus;
            if (process.env.NODE_ENV === 'development') {
                logger.auth('[useSubscription] 🔍 Refetch check after auth completion', { "arg0": {
                        userId: user.id,
                        isAuthenticated,
                        isInitializing,
                        hasNotFetched,
                        isNullStatus,
                        isStalePreAuthStatus,
                        shouldRevalidateStatus,
                        timeSinceLastStatusFetch,
                        needsRefetch,
                        isCurrentlyFetching: fetchingStatus.current,
                        premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'null',
                        lastFetchedUserId: lastFetchedUserId.current,
                        timeSinceLastRefetch
                    } });
            }
            if (needsRefetch) {
                lastRefetchAttempt.current = now;
                // Clear any existing timeout (React Strict Mode protection)
                if (fetchTimeoutRef.current) {
                    if (process.env.NODE_ENV === 'development') {
                        logger.debug('[useSubscription] 🧹 Clearing existing refetch timeout before setting new one');
                    }
                    clearTimeout(fetchTimeoutRef.current);
                    fetchTimeoutRef.current = null;
                }
                // Clear cached user ID to force refetch if user changed
                // NOTE: We don't set premiumStatus to null here because it causes the useEffect to re-run
                // and clear the timeout we're about to set. Instead, we'll let the refetch update the status.
                if (hasNotFetched) {
                    lastFetchedUserId.current = null;
                    // Don't set premiumStatus to null here - it causes useEffect to re-run and clear timeout
                    // setPremiumStatus(null);
                    // Clear ad cache so a prior user's K9/premium state cannot leak to the next session
                    clearAdEligibilityLocalCache();
                }
                setIsLoading(true);
                // Add delay to allow session cookie to propagate and prevent rate limiting
                // Increased delay to prevent 429 errors
                if (process.env.NODE_ENV === 'development') {
                    logger.debug('[useSubscription] ⏱️ Setting timeout for refetch in 1500ms', { "arg0": {
                            userId: user.id,
                            hasNotFetched,
                            isStalePreAuthStatus,
                            isNullStatus,
                            needsRefetch
                        } });
                }
                fetchTimeoutRef.current = setTimeout(() => {
                    if (process.env.NODE_ENV === 'development') {
                        logger.debug('[useSubscription] ⏱️ Timeout fired, proceeding with refetch...', { "arg0": {
                                userId: user.id,
                                isCurrentlyFetching: fetchingStatus.current
                            } });
                    }
                    // We're already inside a useEffect that only runs when authenticated,
                    // so we can proceed directly with the refetch. The API call will handle
                    // authentication via cookies.
                    // Check if a fetch is already in progress
                    if (fetchingStatus.current) {
                        if (process.env.NODE_ENV === 'development') {
                            logger.debug('[useSubscription] ⏭️ Skipping refetch - already fetching', { "arg0": {
                                    userId: user.id,
                                    wasStalePreAuthStatus: isStalePreAuthStatus
                                } });
                        }
                        setIsLoading(false);
                        return;
                    }
                    logger.auth('[useSubscription] 🔄 Auto-refetching subscription status after auth completion', { "arg0": {
                            userId: user.id,
                            hasNotFetched,
                            isNullStatus,
                            wasStalePreAuthStatus: isStalePreAuthStatus,
                            shouldRevalidateStatus,
                            isCurrentlyFetching: fetchingStatus.current
                        } });
                    fetchSubscriptionStatus(true);
                }, 1500); // Increased delay to stagger with other components
                return () => {
                    if (fetchTimeoutRef.current) {
                        if (process.env.NODE_ENV === 'development') {
                            logger.debug('[useSubscription] 🧹 Clearing refetch timeout (useEffect cleanup)');
                        }
                        clearTimeout(fetchTimeoutRef.current);
                        fetchTimeoutRef.current = null;
                    }
                };
            }
        }
    }, [user?.id, isAuthenticated, isInitializing, fetchSubscriptionStatus]);
    // Sync DOM/globals when ad-free from API or bootstrap hint (before /status returns)
    useEffect(() => {
        if (adEligibility) {
            applyAdFreeDomBlocking(adEligibility.reasons);
            return;
        }
        if (!showAds) {
            const reasons: AdFreeReason[] = [];
            if (hasPremium) {
                reasons.push('premium');
            }
            const k9HintEnabled = isViteFlagEnabled('VITE_K9_AD_FREE_ENABLED', import.meta.env.VITE_K9_AD_FREE_ENABLED);
            const userOffline = Boolean((user as { offline?: boolean } | null)?.offline);
            if (user && userHasK9RoleFromUser(user) && (k9HintEnabled || userOffline)) {
                reasons.push('k9_role');
            }
            if (reasons.length > 0) {
                applyAdFreeDomBlocking(reasons);
            }
            return;
        }
        if (!isLoading && isAuthenticated) {
            applyAdFreeDomBlocking([]);
        }
    }, [adEligibility, showAds, hasPremium, user, isLoading, isAuthenticated]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);
    return {
        // Status
        premiumStatus,
        adEligibility,
        hasPremium,
        showAds,
        isAdFree,
        isPremiumUser,
        hasAgencyContract,
        showUpgradeCTA,
        isLoading,
        error,
        // Plans
        plans,
        // History
        history,
        // Actions
        createSubscription,
        cancelSubscription,
        fetchSubscriptionStatus,
        fetchPlans,
        fetchHistory,
        openCustomerPortal,
    };
};
export { useSubscription };
export default useSubscription;
